import type { PricingConfiguration, Milestone } from 'types';

export function calculateMilestoneTotal(
    milestones: Milestone[],
    displayMode: 'amount' | 'percentage',
    baseFee?: number
): number {
    if (displayMode === 'amount') {
        return milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
    } else {
        if (!baseFee) return 0;
        return milestones.reduce((sum, m) => sum + (baseFee * (m.percentage || 0)) / 100, 0);
    }
}

export function calculateTotalPercentage(milestones: Milestone[]): number {
    return milestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
}

export function calculateHourlyEstimate(
    hourlyRate: number,
    estimatedHours: number
): { min: number; max: number } {
    return {
        min: hourlyRate * estimatedHours,
        max: hourlyRate * estimatedHours,
    };
}

export function calculateTotalFee(config: PricingConfiguration): number {
    switch (config.model) {
        case 'fixed':
            return config.fixed?.baseFeeAmount || 0;

        case 'hourly':
            if (!config.hourly) return 0;
            return config.hourly.hourlyRate * config.hourly.estimatedHours;

        case 'milestone':
            if (!config.milestone) return 0;
            return calculateMilestoneTotal(
                config.milestone.milestones,
                config.milestone.displayMode,
                config.milestone.totalFee
            );

        case 'hybrid': {
            if (!config.hybrid) return 0;
            const milestoneTotal = calculateMilestoneTotal(
                config.hybrid.milestones,
                config.hybrid.displayMode,
                config.hybrid.upfrontFee
            );
            const hourlyComponent =
                config.hybrid.hourlyRate && config.hybrid.hourlyCap
                    ? Math.min(config.hybrid.hourlyRate * 100, config.hybrid.hourlyCap)
                    : 0;
            return config.hybrid.upfrontFee + milestoneTotal + hourlyComponent;
        }

        default:
            return 0;
    }
}

export function formatCurrency(amount: number, currency: string): string {
    const currencySymbols: Record<string, string> = {
        CAD: '$',
        USD: '$',
        GBP: '£',
        EUR: '€',
        AUD: '$',
        NZD: '$',
    };

    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function validatePricingConfiguration(config: PricingConfiguration): {
    valid: boolean;
    errors: string[];
    warnings: string[];
} {
    const errors: string[] = [];
    const warnings: string[] = [];

    switch (config.model) {
        case 'fixed':
            if (!config.fixed?.baseFeeAmount || config.fixed.baseFeeAmount <= 0) {
                errors.push('Base fee amount is required for fixed pricing');
            }
            break;

        case 'hourly':
            if (!config.hourly?.hourlyRate || config.hourly.hourlyRate <= 0) {
                errors.push('Hourly rate is required for hourly pricing');
            }
            if (!config.hourly?.estimatedHours || config.hourly.estimatedHours <= 0) {
                errors.push('Estimated hours is required for hourly pricing');
            }
            break;

        case 'milestone':
            if (!config.milestone?.milestones || config.milestone.milestones.length === 0) {
                errors.push('At least one milestone is required for milestone-based pricing');
            } else {
                if (config.milestone.displayMode === 'percentage') {
                    const totalPercentage = calculateTotalPercentage(config.milestone.milestones);
                    if (Math.abs(totalPercentage - 100) > 0.01) {
                        warnings.push(
                            `Milestone percentages sum to ${totalPercentage.toFixed(2)}% (should be 100%)`
                        );
                    }
                }

                config.milestone.milestones.forEach((milestone, index) => {
                    if (!milestone.title.trim()) {
                        errors.push(`Milestone ${index + 1}: Title is required`);
                    }
                    if (
                        config.milestone!.displayMode === 'amount' &&
                        (!milestone.amount || milestone.amount <= 0)
                    ) {
                        errors.push(`Milestone ${index + 1}: Amount is required`);
                    }
                    if (
                        config.milestone!.displayMode === 'percentage' &&
                        (!milestone.percentage || milestone.percentage <= 0)
                    ) {
                        errors.push(`Milestone ${index + 1}: Percentage is required`);
                    }
                });
            }
            break;

        case 'hybrid':
            if (!config.hybrid?.upfrontFee || config.hybrid.upfrontFee < 0) {
                errors.push('Upfront fee is required for hybrid pricing');
            }
            if (!config.hybrid?.milestones || config.hybrid.milestones.length === 0) {
                warnings.push('No milestones defined for hybrid pricing');
            } else {
                if (config.hybrid.displayMode === 'percentage') {
                    const totalPercentage = calculateTotalPercentage(config.hybrid.milestones);
                    if (Math.abs(totalPercentage - 100) > 0.01) {
                        warnings.push(
                            `Milestone percentages sum to ${totalPercentage.toFixed(2)}% (should be 100%)`
                        );
                    }
                }
            }
            break;
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}

export function generateMilestoneTableHTML(
    milestones: Milestone[],
    currency: string,
    displayMode: 'amount' | 'percentage',
    baseFee?: number
): string {
    const visibleMilestones = milestones.filter((m) => m.visible);

    if (visibleMilestones.length === 0) {
        return '<p><em>No milestones configured</em></p>';
    }

    let html = '<table style="width: 100%; border-collapse: collapse; margin: 16px 0;">';
    html += '<thead><tr>';
    html +=
        '<th style="border: 1px solid #ddd; padding: 12px; background-color: #000000; color: white; text-align: left;">Milestone</th>';
    html +=
        '<th style="border: 1px solid #ddd; padding: 12px; background-color: #000000; color: white; text-align: left;">Description</th>';
    html +=
        '<th style="border: 1px solid #ddd; padding: 12px; background-color: #000000; color: white; text-align: left;">Trigger</th>';
    html +=
        '<th style="border: 1px solid #ddd; padding: 12px; background-color: #000000; color: white; text-align: right;">Amount</th>';
    html +=
        '<th style="border: 1px solid #ddd; padding: 12px; background-color: #000000; color: white; text-align: left;">Due Date</th>';
    html += '</tr></thead>';
    html += '<tbody>';

    visibleMilestones.forEach((milestone) => {
        const amount =
            displayMode === 'amount'
                ? milestone.amount || 0
                : baseFee
                  ? (baseFee * (milestone.percentage || 0)) / 100
                  : 0;

        const amountDisplay =
            displayMode === 'percentage'
                ? `${formatCurrency(amount, currency)} (${milestone.percentage}%)`
                : formatCurrency(amount, currency);

        const dueDateDisplay =
            milestone.dueDateType === 'fixed' && milestone.dueDate
                ? milestone.dueDate
                : milestone.relativeDays
                  ? `${milestone.relativeDays} days after signing`
                  : 'TBD';

        html += '<tr>';
        html += `<td style="border: 1px solid #ddd; padding: 12px;">${milestone.title}</td>`;
        html += `<td style="border: 1px solid #ddd; padding: 12px;">${milestone.description || '-'}</td>`;
        html += `<td style="border: 1px solid #ddd; padding: 12px;">${milestone.triggerEvent || '-'}</td>`;
        html += `<td style="border: 1px solid #ddd; padding: 12px; text-align: right; font-weight: 600;">${amountDisplay}</td>`;
        html += `<td style="border: 1px solid #ddd; padding: 12px;">${dueDateDisplay}</td>`;
        html += '</tr>';
    });

    html += '</tbody></table>';
    return html;
}
